import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { Story } from '../types/Story';
import { useTranslation } from 'react-i18next';
import { COLORS } from '../constants/colors';
import defaultCoverImage from '../images/story_cover.jpg';
import { useAuth } from '../context/AuthContext';

interface BookCheckoutProps {}

interface OrderDetails {
  bookTitle: string;
  childName: string;
  coverDesign: string;
  bindingType: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  postalCode: string;
  country: string;
}

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 16px;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 16px 12px;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 32px;
`;

const PageTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #212529;
  margin: 0 0 8px 0;
`;

const PageSubtitle = styled.p`
  font-size: 1rem;
  color: #6c757d;
  margin: 0;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 32px;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const MainSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
`;

const SidebarSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const CardTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #212529;
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  user-select: none;
  -webkit-user-select: none;
  position: relative;
`;

const StoryItem = styled.div<{ $isDragging: boolean }>`
  display: flex;
  gap: 16px;
  padding: 12px;
  background: ${props => props.$isDragging ? '#e3e8ff' : '#f8f9fa'};
  border-radius: 8px;
  align-items: center;
  cursor: grab;
  transition: all 0.2s;
  opacity: ${props => props.$isDragging ? 0.5 : 1};
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  touch-action: none;
  width: 100%;
  box-sizing: border-box;
  
  &:active {
    cursor: grabbing;
  }
  
  &:hover {
    background: #e9ecef;
  }
  
  @media (max-width: 768px) {
    gap: 12px;
    padding: 10px;
  }
`;

const StoryThumbnail = styled.img`
  width: 60px;
  height: 80px;
  object-fit: cover;
  border-radius: 6px;
  flex-shrink: 0;
`;

const StoryInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const StoryTitle = styled.h3`
  font-size: 0.938rem;
  font-weight: 600;
  color: #212529;
  margin: 0 0 4px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StoryMeta = styled.div`
  font-size: 0.813rem;
  color: #6c757d;
`;

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  cursor: grab;
  padding: 4px;
  flex-shrink: 0;
  
  &:active {
    cursor: grabbing;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const DragClone = styled.div<{ $top: number; $left: number }>`
  position: fixed;
  top: ${props => props.$top}px;
  left: ${props => props.$left}px;
  transform: translate(-50%, -50%);
  z-index: 1000;
  pointer-events: none;
  opacity: 0.8;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
  transition: none;
`;

const RemoveButton = styled.button`
  background: transparent;
  border: none;
  color: #dc3545;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  flex-shrink: 0;
  z-index: 10;
  position: relative;
  
  &:hover {
    color: #c82333;
    transform: scale(1.1);
  }
  
  svg {
    width: 20px;
    height: 20px;
    pointer-events: none;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #495057;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  font-size: 0.938rem;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  transition: border-color 0.2s;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: ${COLORS.PRIMARY};
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  font-size: 0.938rem;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  min-height: 80px;
  resize: vertical;
  transition: border-color 0.2s;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: ${COLORS.PRIMARY};
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  font-size: 0.938rem;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: ${COLORS.PRIMARY};
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const PricingRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  font-size: 0.938rem;
  color: #495057;
  
  &.total {
    border-top: 2px solid #dee2e6;
    padding-top: 16px;
    margin-top: 8px;
    font-size: 1.25rem;
    font-weight: 700;
    color: #212529;
  }
`;

const OptionCard = styled.div<{ $selected: boolean }>`
  border: 2px solid ${props => props.$selected ? COLORS.PRIMARY : '#dee2e6'};
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$selected ? '#f0f4ff' : 'white'};
  
  &:hover {
    border-color: ${COLORS.PRIMARY};
  }
`;

const OptionTitle = styled.div`
  font-weight: 600;
  color: #212529;
  margin-bottom: 4px;
`;

const OptionDescription = styled.div`
  font-size: 0.875rem;
  color: #6c757d;
  margin-bottom: 8px;
`;

const OptionPrice = styled.div`
  font-weight: 600;
  color: ${COLORS.PRIMARY};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
  
  @media (max-width: 768px) {
    flex-direction: column-reverse;
  }
`;

const BackButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 500;
  border: 1px solid #dee2e6;
  background: white;
  color: #495057;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #f8f9fa;
    border-color: #adb5bd;
  }
`;

const CheckoutButton = styled.button`
  flex: 2;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  border: none;
  background: ${COLORS.PRIMARY};
  color: white;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    background: ${COLORS.PRIMARY_DARK};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const VatNotice = styled.p`
  font-size: 0.875rem;
  color: #666;
  margin: 12px 0 16px 0;
  padding: 8px 12px;
  background-color: #f8f9fa;
  border-left: 3px solid #007bff;
  border-radius: 4px;
  
  /* Or simpler style: */
  /* 
  text-align: center;
  font-style: italic;
  opacity: 0.8;
  */
`;

const ErrorMessage = styled.span`
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 4px;
  display: block;
`;

const InputWithError = styled(Input)<{ $hasError?: boolean }>`
  border-color: ${props => props.$hasError ? '#dc3545' : '#dee2e6'};
  
  &:focus {
    outline: none;
    border-color: ${props => props.$hasError ? '#dc3545' : COLORS.PRIMARY};
  }
`;

export const BookCheckout: React.FC<BookCheckoutProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user } = useAuth();
  
  // Get selected stories from navigation state
  const [stories, setStories] = useState<Story[]>(location.state?.stories || []);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [touchStartY, setTouchStartY] = useState<number | null>(null);
  const [isDraggingTouch, setIsDraggingTouch] = useState(false);
  const [dragClonePosition, setDragClonePosition] = useState<{ x: number; y: number } | null>(null);
  
  const [orderDetails, setOrderDetails] = useState<OrderDetails>({
    bookTitle: 'My Story Collection',
    childName: '',
    coverDesign: 'classic',
    bindingType: 'softcover',
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'Romania'
  });

  const [emailError, setEmailError] = useState<string>('');

  // Pricing constants
  const HARDCOVER_ADDON = 15.00;
  const SHIPPING_COSTS: Record<string, number> = {
    Romania: 4.0,
    Belgium: 15.0,
    Bulgaria: 15.0,
    Estonia: 17.0,
    Denmark: 15.0,
    Finland: 18.0,
    France: 16.0,
    Greece: 15.0,
    Ireland: 21.0,
    Italy: 17.0,
    Croatia: 14.0,
    Latvia: 16.0,
    Lithuania: 15.0,
    Luxembourg: 15.0,
    Netherlands: 14.0,
    Austria: 14.0,
    Poland: 14.0,
    Portugal: 15.0,
    Slovakia: 15.0,
    Slovenia: 15.0,
    Spain: 17.0,
    Sweden: 19.0,
    Czech_Republic: 14.0,
    Germany: 15.0,
    Hungary: 14.0,
  };

  function getBasePrice(storyCount:number) {
    if(storyCount === 5) return 12;
    if (storyCount === 6 || storyCount === 7) return 13.00;
    if ([8, 9, 10].includes(storyCount)) return 14.00;
    return 14.00; // default base price if other counts
  }

  // Calculate pricing
  const storyCount = stories.length;
  const basePrice = getBasePrice(storyCount);
  // const subtotal = BASE_PRICE + (stories.length * PRICE_PER_STORY);
  const bindingCost = orderDetails.bindingType === 'hardcover' ? HARDCOVER_ADDON : 0;
  const shippingCost = SHIPPING_COSTS[orderDetails.country] || 0;
  const total = basePrice + bindingCost + shippingCost;

  useEffect(() => {
    // If no stories, redirect back
    if (stories.length === 0) {
      navigate('/stories');
    }
  }, [stories, navigate]);

  const handleInputChange = (field: keyof OrderDetails, value: string) => {
    setOrderDetails(prev => ({ ...prev, [field]: value }));
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (value: string) => {
    handleInputChange('email', value);
    // Clear error when user starts typing again
    if (emailError) {
      setEmailError('');
    }
  };

  const handleEmailBlur = () => {
    if (orderDetails.email && !validateEmail(orderDetails.email)) {
      setEmailError(t('checkout.delivery.fields.email.error'));
    } else {
      setEmailError('');
    }
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null) return;
    setDragOverIndex(index);
  };

  const handleDragEnd = () => {
    if (draggedIndex === null || dragOverIndex === null) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newStories = [...stories];
    const draggedStory = newStories[draggedIndex];
    newStories.splice(draggedIndex, 1);
    newStories.splice(dragOverIndex, 0, draggedStory);
    
    setStories(newStories);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  // Touch events for mobile
  const handleTouchStart = (e: React.TouchEvent, index: number) => {
    e.preventDefault();
    const touch = e.touches[0];
    setTouchStartY(touch.clientY);
    setDraggedIndex(index);
    setIsDraggingTouch(true);
    setDragClonePosition({ x: touch.clientX, y: touch.clientY });
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    e.preventDefault();
    if (draggedIndex === null || touchStartY === null) return;
    
    const touch = e.touches[0];
    const touchY = touch.clientY;
    
    // Update clone position
    setDragClonePosition({ x: touch.clientX, y: touchY });
    
    // Calculate which item we're hovering over based on scroll position
    const storyItems = document.querySelectorAll('[data-story-item]');
    storyItems.forEach((item, index) => {
      const rect = item.getBoundingClientRect();
      if (touchY >= rect.top && touchY <= rect.bottom && index !== draggedIndex) {
        setDragOverIndex(index);
      }
    });
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    e.preventDefault();
    if (draggedIndex === null || dragOverIndex === null || draggedIndex === dragOverIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      setTouchStartY(null);
      setIsDraggingTouch(false);
      setDragClonePosition(null);
      return;
    }

    const newStories = [...stories];
    const draggedStory = newStories[draggedIndex];
    newStories.splice(draggedIndex, 1);
    newStories.splice(dragOverIndex, 0, draggedStory);
    
    setStories(newStories);
    setDraggedIndex(null);
    setDragOverIndex(null);
    setTouchStartY(null);
    setIsDraggingTouch(false);
    setDragClonePosition(null);
  };

  const handleRemoveStory = (e: React.MouseEvent | React.TouchEvent, index: number) => {
    e.stopPropagation();
    e.preventDefault();
    setStories(prev => prev.filter((_, i) => i !== index));
    
    // Reset drag state if we're removing the dragged item
    if (index === draggedIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      setTouchStartY(null);
      setIsDraggingTouch(false);
      setDragClonePosition(null);
    }
  };

  const handleCheckout = async () => {
    if (!isFormValid()) return;

    try {
      // Prepare order data
      const orderData = {
        userId: user?.id, // Make sure you have user from auth context
        storyIds: stories.map(s => s.id),
        orderDetails,
        deliveryInfo: orderDetails, // Using the same object for simplicity
        pricing: {
          bindingCost,
          total
        }
      };

      // Call backend to create order and get Stripe checkout URL
      const response = await fetch(`${process.env.REACT_APP_API_URL}/books/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Add auth token if needed: 'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(orderData),
      });

      const result = await response.json();

      if (result.success && result.data.checkoutUrl) {
        // Redirect to Stripe checkout
        window.location.href = result.data.checkoutUrl;
      } else {
        alert('Failed to create order: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error creating order:', error);
      alert('An error occurred while processing your order. Please try again.');
    }
  };

  const isFormValid = () => {
    // todo: uncomment below when ordering the book is available
    // return false;
    return (
      orderDetails.name.trim() !== '' &&
      orderDetails.email.trim() !== '' &&
      validateEmail(orderDetails.email) &&
      orderDetails.address.trim() !== '' &&
      orderDetails.city.trim() !== '' &&
      orderDetails.postalCode.trim() !== '' &&
      stories.length > 0
    );
  };

  if (stories.length === 0) {
    return null;
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>{t('checkout.page.title')}</PageTitle>
        <PageSubtitle>{t('checkout.page.subtitle')}</PageSubtitle>
      </PageHeader>

      <ContentGrid>
        <MainSection>
          {/* Selected Stories */}
          <Card>
            <CardTitle>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {t('checkout.selectedStories.title')} ({stories.length})
            </CardTitle>
            
            <StoryList>
              {stories.map((story, index) => (
                <StoryItem 
                  key={story.id}
                  data-story-item
                  draggable
                  $isDragging={draggedIndex === index}
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  onDragLeave={handleDragLeave}
                  onTouchStart={(e) => handleTouchStart(e, index)}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  style={{
                    border: dragOverIndex === index ? `2px dashed ${COLORS.PRIMARY}` : 'none'
                  }}
                >
                  <DragHandle>
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <circle cx="9" cy="5" r="1.5"/>
                      <circle cx="9" cy="12" r="1.5"/>
                      <circle cx="9" cy="19" r="1.5"/>
                      <circle cx="15" cy="5" r="1.5"/>
                      <circle cx="15" cy="12" r="1.5"/>
                      <circle cx="15" cy="19" r="1.5"/>
                    </svg>
                  </DragHandle>
                  <StoryThumbnail 
                    src={story.imageUrls?.[0] || defaultCoverImage} 
                    alt={story.title}
                  />
                  <StoryInfo>
                    <StoryTitle>{story.title}</StoryTitle>
                    <StoryMeta>
                      {t(`storyTypes.${story.storyType}`)} • {t(`ageRanges.${story.ageRange}`)}
                    </StoryMeta>
                  </StoryInfo>
                  <RemoveButton
                    onClick={(e) => handleRemoveStory(e, index)}
                    onTouchEnd={(e) => handleRemoveStory(e, index)}
                    title={t('checkout.selectedStories.removeStory')} 
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <line x1="18" y1="6" x2="6" y2="18" strokeWidth="2" strokeLinecap="round"/>
                      <line x1="6" y1="6" x2="18" y2="18" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </RemoveButton>
                </StoryItem>
              ))}
              
              {/* Floating clone for touch drag */}
              {isDraggingTouch && draggedIndex !== null && dragClonePosition && stories[draggedIndex] && (
                <DragClone $top={dragClonePosition.y} $left={dragClonePosition.x}>
                  <StoryItem $isDragging={false}>
                    <DragHandle>
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <circle cx="9" cy="5" r="1.5"/>
                        <circle cx="9" cy="12" r="1.5"/>
                        <circle cx="9" cy="19" r="1.5"/>
                        <circle cx="15" cy="5" r="1.5"/>
                        <circle cx="15" cy="12" r="1.5"/>
                        <circle cx="15" cy="19" r="1.5"/>
                      </svg>
                    </DragHandle>
                    <StoryThumbnail 
                      src={stories[draggedIndex].imageUrls?.[0] || defaultCoverImage} 
                      alt={stories[draggedIndex].title}
                    />
                    <StoryInfo>
                      <StoryTitle>{stories[draggedIndex].title}</StoryTitle>
                      <StoryMeta>
                        {t(`storyTypes.${stories[draggedIndex].storyType}`)} • {t(`ageRanges.${stories[draggedIndex].ageRange}`)}
                      </StoryMeta>
                    </StoryInfo>
                  </StoryItem>
                </DragClone>
              )}
            </StoryList>
          </Card>

          {/* Book Customization */}
          <Card>
            <CardTitle>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="12" cy="12" r="3" strokeWidth="2"/>
                <path d="M12 1v6m0 6v6m9-9h-6m-6 0H3" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              {t('checkout.customization.title')} 
            </CardTitle>

            <FormGroup>
              <Label>{t('checkout.customization.fields.bookTitle.label')} </Label>
              <Input
                type="text"
                value={orderDetails.bookTitle}
                onChange={(e) => handleInputChange('bookTitle', e.target.value)}
                placeholder={t('checkout.customization.fields.bookTitle.placeholder')}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>{t('checkout.customization.fields.childName.label')}</Label>
              <Input
                type="text"
                value={orderDetails.childName}
                onChange={(e) => handleInputChange('childName', e.target.value)}
                placeholder={t('checkout.customization.fields.childName.placeholder')}
              />
            </FormGroup>

            <FormGroup>
              <Label>{t('checkout.customization.fields.bindingType.label')}</Label>

              <OptionCard
                $selected={orderDetails.bindingType === 'softcover'}
                onClick={() => handleInputChange('bindingType', 'softcover')}
              >
                <OptionTitle>
                  {t('checkout.customization.fields.bindingType.softcover.title')}
                </OptionTitle>
                <OptionDescription>
                  {t('checkout.customization.fields.bindingType.softcover.description')}
                </OptionDescription>
                <OptionPrice>
                  {t('checkout.customization.fields.bindingType.softcover.price')}
                </OptionPrice>
              </OptionCard>

              {/* <OptionCard
                $selected={orderDetails.bindingType === 'hardcover'}
                onClick={() => handleInputChange('bindingType', 'hardcover')}
              >
                <OptionTitle>
                  {t('checkout.customization.fields.bindingType.hardcover.title')}
                </OptionTitle>
                <OptionDescription>
                  {t('checkout.customization.fields.bindingType.hardcover.description')}
                </OptionDescription>
                <OptionPrice>
                  {t('checkout.customization.fields.bindingType.hardcover.price')}{HARDCOVER_ADDON.toFixed(2)}
                </OptionPrice>
              </OptionCard> */}
            </FormGroup>

          </Card>

          {/* Delivery Information */}
          <Card>
            <CardTitle>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <rect x="1" y="3" width="15" height="13" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 8h5l3 3v5h-2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="5.5" cy="18.5" r="2.5" strokeWidth="2"/>
                <circle cx="18.5" cy="18.5" r="2.5" strokeWidth="2"/>
              </svg>
              {t('checkout.delivery.title')}
            </CardTitle>

            <FormGroup>
              <Label>{t('checkout.delivery.fields.fullName.label')}</Label>
              <Input
                type="text"
                value={orderDetails.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder={t('checkout.delivery.fields.fullName.placeholder')}
                required
              />
            </FormGroup>

            <FormRow>
              <FormGroup>
                <Label>{t('checkout.delivery.fields.email.label')}</Label>
                <InputWithError
                  type="email"
                  value={orderDetails.email}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  onBlur={handleEmailBlur}
                  placeholder={t('checkout.delivery.fields.email.placeholder')}
                  $hasError={!!emailError}
                  required
                />
                {emailError && <ErrorMessage>{emailError}</ErrorMessage>}
              </FormGroup>
              <FormGroup>
                <Label>{t('checkout.delivery.fields.phone.label')}</Label>
                <Input
                  type="tel"
                  value={orderDetails.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder={t('checkout.delivery.fields.phone.placeholder')}
                />
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label>{t('checkout.delivery.fields.streetAddress.label')}</Label>
              <TextArea
                value={orderDetails.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                placeholder={t('checkout.delivery.fields.streetAddress.placeholder')}
                required
              />
            </FormGroup>

            <FormRow>
              <FormGroup>
                <Label>{t('checkout.delivery.fields.city.label')}</Label>
                <Input
                  type="text"
                  value={orderDetails.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  placeholder={t('checkout.delivery.fields.city.placeholder')}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>{t('checkout.delivery.fields.postalCode.label')}</Label>
                <Input
                  type="text"
                  value={orderDetails.postalCode}
                  onChange={(e) => handleInputChange('postalCode', e.target.value)}
                  placeholder={t('checkout.delivery.fields.postalCode.placeholder')}
                  required
                />
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label>{t('checkout.delivery.fields.country.label')}</Label>
              <Select
                value={orderDetails.country}
                onChange={(e) => handleInputChange('country', e.target.value)}
              >
                {Object.keys(SHIPPING_COSTS).sort().map(country => (
                  <option key={country} value={country}>
                    {country.replace(/_/g, ' ')}
                  </option>
                ))}
              </Select>
            </FormGroup>
          </Card>
        </MainSection>

        {/* Order Summary Sidebar */}
        <SidebarSection>
          <Card>
            <CardTitle>{t('checkout.orderSummary.title')}</CardTitle>
            
            <PricingRow>
              <span>{t('checkout.orderSummary.pricing.basePrice')}</span>
              <span>€{basePrice.toFixed(2)}</span>
            </PricingRow>
            <PricingRow>
              <span>{t('checkout.orderSummary.pricing.shipping')}</span>
              <span>€{shippingCost.toFixed(2)}</span>
            </PricingRow>
            
            {/* <PricingRow>
              <span>{stories.length} {t('checkout.orderSummary.pricing.stories')} × €{PRICE_PER_STORY.toFixed(2)}</span>
              <span>€{(stories.length * PRICE_PER_STORY).toFixed(2)}</span>
            </PricingRow> */}
            
            {bindingCost > 0 && (
              <PricingRow>
                <span>{t('checkout.orderSummary.pricing.hardcoverUpgrade')}</span>
                <span>€{bindingCost.toFixed(2)}</span>
              </PricingRow>
            )}
            
            <PricingRow className="total">
              <span>{t('checkout.orderSummary.pricing.total')}</span>
              <span>€{total.toFixed(2)}</span>
            </PricingRow>

            <VatNotice>
              {t('checkout.orderSummary.pricing.vatNotice')}
            </VatNotice>

            <ActionButtons>
              <BackButton onClick={() => navigate(-1)}>
                {t('checkout.orderSummary.actions.back')}
              </BackButton>
              <CheckoutButton
                onClick={handleCheckout}
                disabled={!isFormValid()}
              >
                {t('checkout.orderSummary.actions.checkout')}
              </CheckoutButton>
            </ActionButtons>
          </Card>

          <Card style={{ background: '#f0f4ff', border: `1px solid ${COLORS.PRIMARY}` }}>
            <div style={{ fontSize: '0.875rem', color: '#495057' }}>
              <strong style={{ color: COLORS.PRIMARY }}>{t('checkout.orderSummary.deliveryInfo.title')}</strong>
              <p style={{ margin: '8px 0 0 0', lineHeight: '1.6' }}>
                {t('checkout.orderSummary.deliveryInfo.description')}
              </p>
            </div>
          </Card>
        </SidebarSection>
      </ContentGrid>
    </PageContainer>
  );
};